// Hero slider — active-slide management. Keyed to the engine's own `aria-hidden` state on each
// <slideshow-slide> (the engine sets it; we never fight it). Three jobs:
//
//   1. Mirror `inert` onto aria-hidden="true" slides so the controls inside an OFF-screen slide
//      (CTAs, product-card links, mosaic tile links, the comparison handle, the drawer trigger) leave
//      the keyboard tab order and the a11y tree together — closing the carousel focus-trap
//      (WCAG 2.4.3 Focus Order / 4.1.2). Skipped in "peek/hints" mode, where aria-hidden peeking
//      slides are intentionally still clickable to navigate.
//   2. Pause every off-screen slide's <video> and play the active slide's autoplay video, so a hero
//      with several video slides never runs them all at once.
//   3. Swap the engine's content-level `aria-live` (which announces the WHOLE rich slide on every
//      auto-advance) for a concise visually-hidden "Slide X of N" status — far less verbose for SR
//      users (WCAG 4.1.3). Hero-scoped, so the shared slideshow engine is untouched for other sections.
//
// Additive and engine-safe: we only toggle `inert`, play/pause videos, and manage our own status node.
// No focus is moved, no navigation is touched. Re-binds on shopify:section:load.

(() => {
  const setVideos = (slide, play) => {
    slide.querySelectorAll('video').forEach((video) => {
      if (play) {
        if (video.autoplay) {
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        }
      } else {
        video.pause();
      }
    });
  };

  const bind = (slideshow) => {
    if (slideshow.dataset.heroActiveBound) return;
    slideshow.dataset.heroActiveBound = '1';

    // In peek/hints mode the aria-hidden peeking slides must stay clickable, so don't inert them.
    const manageInert = !slideshow.classList.contains('slideshow--with-hints');
    const slides = [...slideshow.querySelectorAll('slideshow-slide')];

    // Replace the engine's verbose content-level live region with a bounded status node.
    let statusEl = null;
    if (slideshow.getAttribute('aria-live')) {
      slideshow.removeAttribute('aria-live');
      statusEl = document.createElement('p');
      statusEl.className = 'visually-hidden';
      statusEl.setAttribute('aria-live', 'polite');
      statusEl.setAttribute('role', 'status');
      slideshow.insertAdjacentElement('afterend', statusEl);
    }

    const apply = (slide, index) => {
      const hidden = slide.getAttribute('aria-hidden') === 'true';
      if (manageInert) slide.toggleAttribute('inert', hidden);
      setVideos(slide, !hidden);
      if (!hidden && statusEl) {
        // Count only slides shown for the current viewport — hero-slide-visibility.js hides the rest
        // with [hidden], so the announced position/total must track the VISIBLE carousel, not all blocks.
        const visible = slides.filter((s) => !s.hasAttribute('hidden'));
        const pos = visible.indexOf(slide);
        if (pos !== -1) statusEl.textContent = `Slide ${pos + 1} of ${visible.length}`;
      }
    };

    slides.forEach((slide, index) => {
      apply(slide, index);
      const observer = new MutationObserver(() => apply(slide, index));
      observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
  };

  const init = (root) => {
    (root || document).querySelectorAll('slideshow-component.hero-slider').forEach(bind);
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root && typeof root.querySelectorAll === 'function') init(root);
  });
})();
